/**
 * Mode S (NOW/ASAP) open-hours gate — customer offer side.
 *
 * Spec lock: evaluate **on-spot `PickupPoint.openHoursJson` only**.
 * Do **not** fall back to Tenant.pickupSlotHoursJson or silent 08–20.
 * Prefer server `isOpenNow` from ListPickupPointsForKioskUseCase DTO when present.
 * Missing / unparseable / omitted hours → closed (fail closed). Never soft-open.
 */

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
type DayKey = (typeof DAY_KEYS)[number];

const DEFAULT_TIME_ZONE = 'Europe/Prague';

export type PickupOpenHoursJson = Record<string, unknown>;

export interface PickupPointOpenGateInput {
  /** Server-authoritative gate when present (preferred). */
  readonly isOpenNow?: boolean | null;
  /** On-spot weekly hours JSON from pickup-points list DTO. */
  readonly openHoursJson?: PickupOpenHoursJson | null;
  /** Clock override for tests. */
  readonly now?: Date;
}

function parseHm(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return null;
  }
  return hour * 60 + minute;
}

function localParts(
  date: Date,
  timeZone: string,
): { dayKey: DayKey; minutesOfDay: number } {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const weekday = parts.find((p) => p.type === 'weekday')?.value.toLowerCase() ?? 'mon';
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  const dayMap: Record<string, DayKey> = {
    sun: 'sun',
    mon: 'mon',
    tue: 'tue',
    wed: 'wed',
    thu: 'thu',
    fri: 'fri',
    sat: 'sat',
  };
  const dayKey = dayMap[weekday.slice(0, 3)] ?? 'mon';
  return { dayKey, minutesOfDay: hour * 60 + minute };
}

function resolveWeeklyFromPointHours(
  source: PickupOpenHoursJson | null | undefined,
): {
  weekly: Record<string, { open: string; close: string }[]> | null;
  timeZone: string;
} {
  if (source == null || typeof source !== 'object') {
    return { weekly: null, timeZone: DEFAULT_TIME_ZONE };
  }
  const timeZone =
    typeof source['timezone'] === 'string' && source['timezone'].length > 0
      ? source['timezone']
      : DEFAULT_TIME_ZONE;
  const weeklyRaw = source['weekly'];
  if (weeklyRaw == null || typeof weeklyRaw !== 'object') {
    return { weekly: null, timeZone };
  }
  const weekly: Record<string, { open: string; close: string }[]> = {};
  let anyInterval = false;
  for (const day of DAY_KEYS) {
    const intervals = (weeklyRaw as Record<string, unknown>)[day];
    if (!Array.isArray(intervals)) {
      weekly[day] = [];
      continue;
    }
    const parsed = intervals
      .map((entry) => {
        if (entry == null || typeof entry !== 'object') {
          return null;
        }
        const open = (entry as { open?: unknown }).open;
        const close = (entry as { close?: unknown }).close;
        if (typeof open !== 'string' || typeof close !== 'string') {
          return null;
        }
        return { open, close };
      })
      .filter((entry): entry is { open: string; close: string } => entry !== null);
    weekly[day] = parsed;
    if (parsed.length > 0) {
      anyInterval = true;
    }
  }
  if (!anyInterval) {
    return { weekly: null, timeZone };
  }
  return { weekly, timeZone };
}

function isInstantWithinWeekly(
  instant: Date,
  weekly: Record<string, { open: string; close: string }[]>,
  timeZone: string,
): boolean {
  const { dayKey, minutesOfDay } = localParts(instant, timeZone);
  const intervals = weekly[dayKey] ?? [];
  return intervals.some((interval) => {
    const open = parseHm(interval.open);
    const close = parseHm(interval.close);
    if (open === null || close === null) {
      return false;
    }
    if (close <= open) {
      return minutesOfDay >= open || minutesOfDay < close;
    }
    return minutesOfDay >= open && minutesOfDay < close;
  });
}

/**
 * Mode S offer gate: true only when the on-spot point is open **now**.
 * Prefer server `isOpenNow`; else evaluate `openHoursJson` with **no** tenant/08–20 fallback.
 * Omitted / null / unusable hours → closed (MUST-fill fail closed — never soft-open).
 */
export function isPickupPointOpenNow(input: PickupPointOpenGateInput): boolean {
  if (input.isOpenNow === true) {
    return true;
  }
  if (input.isOpenNow === false) {
    return false;
  }
  if (input.openHoursJson === undefined) {
    // Hours omitted from payload — fail closed (never soft-open Mode S).
    return false;
  }
  const resolved = resolveWeeklyFromPointHours(input.openHoursJson);
  if (resolved.weekly === null) {
    // Hours field present (incl. explicit null) but unusable → fail closed (MUST-fill).
    return false;
  }
  const now = input.now ?? new Date();
  return isInstantWithinWeekly(now, resolved.weekly, resolved.timeZone);
}

/**
 * Resolve the on-spot (or selected) point used for Mode S NOW gating.
 */
export function resolveModeSOpenGatePoint<
  T extends {
    readonly id: number;
    readonly isOnSpot: boolean;
    readonly isOpenNow?: boolean | null;
    readonly openHoursJson?: PickupOpenHoursJson | null;
  },
>(points: readonly T[], selectedPointId: number | null): T | null {
  if (selectedPointId !== null) {
    const selected = points.find((p) => p.id === selectedPointId);
    if (selected !== undefined) {
      return selected;
    }
  }
  return points.find((p) => p.isOnSpot) ?? points[0] ?? null;
}
